'use client';

import { PrudsysCategoryToBrandsDocument } from '@packages/gql/generated/shopping/PrudsysCategoryToBrandsDocument';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { BrandsRecoSliderProps, RequestParams, RdeType } from '../../../types';
import { RecoBrandCard } from '../../RecoBrandCard/RecoBrandCard.client';
import { useBrandsRecoTracking } from '../../RecoBrandCard/useBrandsRecoTracking';
import { RecoCarousel } from '../../RecoCarousel/RecoCarousel.client';

export type PrudsysCategoryToBrandsProps = Pick<RequestParams, 'categoryId'> &
  BrandsRecoSliderProps;

/* GraphQL */ `
  query PrudsysCategoryToBrands($categoryId: String!, $locale: String!) {
    prudsysCategoryToBrands(categoryId: $categoryId, locale: $locale) {
      title
      type
      recommendations {
        ...BrandRecommendationFragment
      }
    }
  }
`;

const RDE_TYPE: RdeType = 'prudsysCategoryToBrands';

/**
 * SimilarProductsRecoSlider component description displayed in storybook
 * */
export const PrudsysCategoryToBrandsRecoSlider = ({
  backgroundColor,
  categoryId,
  fetchOnThreshold = false,
  preferTitle = false,
  title,
}: PrudsysCategoryToBrandsProps) => {
  const { isInThreshold, isInView, recoEl } = useRecoElement();
  const { locale } = useIntl();

  const [{ data, error }] = useQuery({
    query: PrudsysCategoryToBrandsDocument,
    variables: {
      categoryId,
      locale,
    },
    context: useMemo(() => ({ suspense: false }), []),
    pause: fetchOnThreshold ? !isInThreshold : false,
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.prudsysCategoryToBrands.recommendations : undefined;

  const rdeTypeAbbr = data?.prudsysCategoryToBrands.type;

  useBrandsRecoTracking({
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    title: data?.prudsysCategoryToBrands.title || '',
    trackingCondition: isInView && !!recommendations && recommendations.length > 0,
    recommendations,
  });

  if ((error && !recommendations) || (recommendations && recommendations.length === 0)) {
    return null;
  }

  return (
    <RecoCarousel
      title={preferTitle ? title : data?.prudsysCategoryToBrands.title || title}
      // This is for preventing unnecessary title component with skeleton.
      preferTitle={preferTitle}
      loadingState={!recommendations}
      backgroundColor={backgroundColor}
      ref={recoEl}
      type="BRAND"
    >
      {recommendations?.map((recommendation, idx) => (
        <RecoBrandCard
          // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={idx}
          position={idx + 1}
          recommendationData={recommendation}
          rdeTypeAbbr={rdeTypeAbbr}
        />
      ))}
    </RecoCarousel>
  );
};
